import { useContext, useEffect, useRef, useState } from 'react';

import { SignalRContext } from 'contexts/signalR';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import { CallScribeActiveTranscriptCallSelector } from 'store/callScribe/selectors';
import { mergeTranscriptSegments } from 'store/callScribe';
import { normaliseSpeaker } from 'services/callScribe/participants';
import { getCallScribeTranscriptSegments } from 'services/callScribe';
import { ICallScribeTranscriptSegment, ILiveTranscriptSegment } from 'services/callScribe/types';

export const toLiveSegment = (row: ICallScribeTranscriptSegment): ILiveTranscriptSegment => ({
  seq: row.seq,
  speaker: normaliseSpeaker(row.speaker),
  participantId: row.participantId ?? null,
  text: row.text,
  startMs: row.startMs ?? null,
  endMs: row.endMs ?? null,
  committed: true,
});

export const useLiveTranscriptBackfill = (): void => {
  const dispatch = useAppDispatch();
  const callId = useAppSelector(CallScribeActiveTranscriptCallSelector);
  const { connection } = useContext(SignalRContext);
  const [generation, setGeneration] = useState(0);
  const fetchedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!connection) return undefined;
    const bump = (): void => { setGeneration(n => n + 1); };
    connection.onreconnected(bump);
    return undefined;
  }, [connection]);

  useEffect(() => {
    if (typeof callId !== 'number') return;

    const key = `${callId}|${generation}`;
    if (fetchedRef.current === key) return;
    fetchedRef.current = key;

    void (async () => {
      const res = await getCallScribeTranscriptSegments(callId).catch(() => undefined);
      const rows = res?.data;
      if (!Array.isArray(rows) || rows.length === 0) return;
      dispatch(mergeTranscriptSegments({ callId, segments: rows.map(toLiveSegment) }));
    })();
  }, [callId, generation, dispatch]);
};
